<template>
	<CDialog
		title="Form Details"
		:visible="show"
		:is-loading="isLoading"
		custom-class="form-details-dialog"
		@done="$emit('close')"
		@closed="$emit('closed')"
	>
		<template #headerButton>
			<CButton v-if="canViewOrganizationAuditLog" class="button button--secondary" @click="$emit('org-audit-log')">
				<template #icon>
					<Inline svg="audit-log" />
				</template>
				Audit Log
			</CButton>
		</template>
		<CFormDetails
			v-if="!isLoading"
			:id="formId"
			:title="meta.Title"
			:name="meta.Name"
			:url="url"
			:folder="folderName"
			:theme="serviceData.Theme"
			:created-by="serviceData.CreatedBy"
			:created-on="parseDate(serviceData.CreatedOn)"
			:last-modified-by="serviceData.LastModifiedBy"
			:last-modified-on="parseDate(serviceData.LastModifiedOn)"
			:entries="meta.Entries"
			:storage="serviceData.Storage"
			:payment="serviceData.Payment"
			:availability-start="parseDate(serviceData.AvailabilityStart)"
			:availability-end="parseDate(serviceData.AvailabilityEnd)"
			:shared-as-template="serviceData.SharedAsTemplate"
			:encryption="serviceData.Encryption"
			:live-custom-templates="serviceData.LiveCustomTemplates"
			:references="serviceData.References"
			:referenced-by="serviceData.ReferencedBy"
		/>
	</CDialog>
</template>

<!-- container component -->
<script lang="ts">
	import type { DeepReadonly } from 'vue';
	import { computed, defineComponent, ref, watch, inject } from 'vue';
	import CDialog from 'src/components/dialogs/Dialog.vue';
	import CFormDetails from './../app/FormDetails.vue';
	import CButton from 'src/components/interaction/Button.vue';
	import clickOutside from 'src/utilities/clickOutside';
	import { getFormDetails } from '@cognitoforms/api/services/form-details-service';
	import type { FormDetails } from '@cognitoforms/types/server-types/services/forms/form-details';
	import { globalStore } from 'src/stores/global-store';
	import type { UserFormMeta } from '@cognitoforms/types/server-types/core/services/user-form-meta';
	import { organizationAuditLogKey } from 'src/utilities/injection-keys';
	import { OrganizationAuditLogItemType } from '@cognitoforms/types/server-types/model/organization-audit-log-item-type';

	export default defineComponent({
		name: 'CFormDetailsDialog',
		components: { CDialog, CFormDetails, CButton },
		directives: { clickOutside },
		props: {
			formId: {
				type: String,
				required: true
			},
			show: {
				type: Boolean,
				default: false
			}
		},
		emits: ['close', 'closed', 'org-audit-log'],
		setup(props) {
			const isLoading = ref(true);
			const meta = ref<DeepReadonly<UserFormMeta>>({} as UserFormMeta);
			const serviceData = ref<FormDetails>({} as FormDetails);

			const url = computed(() => {
				const formUrlName = meta.value.UrlName ? meta.value.PreservedCaseUrlName : meta.value.InternalName;
				return `.../${globalStore.organization.PreservedCaseCode}/${formUrlName}`;
			});

			const folderName = computed(() => {
				if (meta.value.FolderId)
					return globalStore.folders[meta.value.FolderId]?.Name;
				return null;
			});

			const parseDate = (input: string | Date) => {
				if (input === null)
					return null;
				const date = new Date(input);
				return isNaN(date.getTime()) ? null : date;
			};

			const initialize = () => {
				isLoading.value = true;
				if (!props.show)
					return;

				meta.value = globalStore.forms[props.formId];

				getFormDetails(props.formId).then((response) => {
					serviceData.value = response;
					isLoading.value = false;
				});
			};

			const organizationAuditLog = inject(organizationAuditLogKey);
			const canViewOrganizationAuditLog = organizationAuditLog.canViewOrganizationAuditLogForEntity(props.formId, OrganizationAuditLogItemType.Form);

			watch(() => props.show, () => initialize(), { immediate: true });

			return {
				meta,
				folderName,
				serviceData,
				isLoading,
				parseDate,
				url,
				canViewOrganizationAuditLog
			};
		}
	});
</script>

<style lang="scss">
	.form-details-dialog {
		max-width: 550px;
	}
</style>